import React from 'react';
import styled from 'styled-components';
import { PostalCodeValidation } from '../../../app/common/fields/postalcode/PostalCodeValidation';
import Icon from '../../../app/common/components/Icon';
import ICONS from '../../../app/common/constants/icons';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';
import { TextHelpers } from '../../../utils/textHelpers';
import { IAddress } from './address.interface';
import { injectIntl, InjectedIntlProps } from 'react-intl';

const FormattedAddressLine = styled.div`
    margin-top: 5px;
    word-break: break-word;
    display: flex;
    align-items: center;
`;

export interface IAddressFormatProps {
    address: IAddress;
    displayIcon: boolean;
    locale: SupportedLanguage;
}

function AddressFormat({
    address,
    displayIcon,
    locale,
    intl,
}: IAddressFormatProps & InjectedIntlProps) {
    const phoneAriaLabel = intl.formatMessage(
        TranslationHelpers.getMessageDescriptor('label.phone_number'),
    );
    const mobileAriaLabel = intl.formatMessage(
        TranslationHelpers.getMessageDescriptor('label.mobile_phone'),
    );
    const faxAriaLabel = intl.formatMessage(
        TranslationHelpers.getMessageDescriptor('label.fax_number'),
    );

    let displayPostalCode = address.postalCode;
    if (
        PostalCodeValidation.validateJPPostalCode(address.postalCode) &&
        !PostalCodeValidation.isOverSeasPostalCode(address.postalCode)
    ) {
        displayPostalCode = '〒' + address.postalCode;
    }

    return (
        <div data-qa-id="address-summary">
            {displayPostalCode && <FormattedAddressLine>{displayPostalCode}</FormattedAddressLine>}
            {(address.state || address.city) && (
                <FormattedAddressLine data-qa-id="address-summary-state-city">
                    {TextHelpers.getStateDisplayValue(address.state, locale)} {address.city}
                </FormattedAddressLine>
            )}
            {address.addressLine1 && (
                <FormattedAddressLine data-qa-id="address-summary-addressLine1">
                    {address.addressLine1}
                </FormattedAddressLine>
            )}
            {(address.companyName || address.positionName) && (
                <FormattedAddressLine data-qa-id="address-summary-company-name-jobTitle">
                    {address.companyName} {address.positionName}
                </FormattedAddressLine>
            )}
            {address.phoneNumber && (
                <FormattedAddressLine data-qa-id="address-summary-phoneNumber">
                    {displayIcon ? (
                        <Icon
                            icon={ICONS.PHONE}
                            fill="#7F7F7F"
                            size={'20'}
                            viewBoxMax={'35'}
                            fillRule="evenodd"
                            clipRule="evenodd"
                            ariaLabel={phoneAriaLabel}
                            tabIndex={0}
                        />
                    ) : (
                        TranslationHelpers.getFormattedMessage('address.list.phone_number')
                    )}
                    {address.phoneNumber}
                </FormattedAddressLine>
            )}
            {address.mobilePhoneNumber && (
                <FormattedAddressLine data-qa-id="address-summary-phoneNumber">
                    {displayIcon ? (
                        <Icon
                            icon={ICONS.MOBILEPHONE}
                            fill="#7F7F7F"
                            size={'20'}
                            viewBoxMax={'35'}
                            fillRule="evenodd"
                            clipRule="evenodd"
                            ariaLabel={mobileAriaLabel}
                            tabIndex={0}
                        />
                    ) : (
                        TranslationHelpers.getFormattedMessage('address.list.mobile_number')
                    )}
                    {address.mobilePhoneNumber}
                </FormattedAddressLine>
            )}
            {address.faxNumber && (
                <FormattedAddressLine data-qa-id="address-summary-faxNumber">
                    {displayIcon ? (
                        <Icon
                            icon={ICONS.FAX}
                            fill="#7F7F7F"
                            size={'20'}
                            viewBoxMax={'20'}
                            fillRule="evenodd"
                            clipRule="evenodd"
                            ariaLabel={faxAriaLabel}
                            tabIndex={0}
                        />
                    ) : (
                        TranslationHelpers.getFormattedMessage('label.fax_number')
                    )}
                    {address.faxNumber}
                </FormattedAddressLine>
            )}
        </div>
    );
}

export default injectIntl(AddressFormat);
